import React, { useContext, useEffect } from "react";

import { useMeetingManager } from "amazon-chime-sdk-component-library-react";

import { MeetingInfoProvider } from "../../context/meeting-info-context";
import ChimeMessageProvider from "../../context/chime-message-context";
import MeetingStatusManager from "./components/meeting-status-manager";
import { MeetingJoinType, MeetingLayout } from "../../types";
import { useGetMeetingJoinInfo, useJoinMeeting } from "../../store";

import { ConnectIoTContext } from "../../websocket/connect-iot";
import { JoinResponse } from "../../services/chime-service";
import {
  HeadsetLayout,
  MetroLayout,
  MobileUploadLayout,
  RemoteLayout,
  TrainingLayout,
} from "./layouts";
import MeetingStatusPage from "./meeting-status-page";
import MeetingLayoutSelectionPage from "./components/meeting-layout-selection-page";
import UpdateUserInfoPage from "./components/update-user-info-page";
import MeetingLoadingPage from "./meeting-loading-page";

type ExtendedMeetingLayout = MeetingLayout | "headset" | "phone" | "training";

// Detect if running on mobile device
const isMobile = () => navigator.userAgent.includes("Mobile");

// Detect if running on a RealWare headset device (based on known screen dimensions)
const isHeadset = () => {
  let widthMatches = Math.abs(window.screen.width - 853) < 10;
  widthMatches = widthMatches || Math.abs(window.screen.width - 1003) < 10;

  // Check for known RealWare screen heights (480px or 564px with ±10px tolerance)
  let heightMatches = Math.abs(window.screen.height - 480) < 10;
  heightMatches = heightMatches || Math.abs(window.screen.height - 564) < 10;

  return widthMatches && heightMatches;
};

// A temporary helper function to map the selected layout to a meeting join type. This is used to determine the appropriate meeting type for the join request.
const typeFromLayout = (
  layout: ExtendedMeetingLayout | null,
): MeetingJoinType => {
  switch (layout) {
    case "headset":
      return MeetingJoinType.Headset;
    case "phone":
      return MeetingJoinType.Phone;
    default:
      return MeetingJoinType.Web;
  }
};

/**
 * Props interface for the MeetingPage component
 */
interface MeetingPageProps {
  meetingId: string;
  userId: string;
  viaQRCode?: boolean;
  joinCode?: string;
  layout?: MeetingLayout;
}

/**
 * MeetingPage Component
 *
 * Main page component for handling telehealth meetings. This component:
 * - Manages different meeting types (Metro, Mobile, Remote, Training, etc.)
 * - Handles device-specific logic for RealWare devices
 * - Manages meeting join process and user data
 * - Provides context providers for meeting information and Chime messaging
 * - Renders appropriate meeting interface based on meeting type
 */
const MeetingPage: React.FC<MeetingPageProps> = ({
  meetingId,
  userId,
  viaQRCode = false,
  joinCode: joinCodeProp,
  layout,
}: MeetingPageProps) => {
  const { setGuestCredentials } = useContext(ConnectIoTContext);

  const [selectedLayout, setSelectedLayout] = React.useState<
    ExtendedMeetingLayout | null
  >(() => {
    // Mobile and QR joins always use a device-specific layout.
    if (isMobile() || viaQRCode) {
      return isHeadset() ? "headset" : "phone";
    }

    return layout as ExtendedMeetingLayout | null;
  });

  const [joinCode, setJoinCode] = React.useState<string>(joinCodeProp || "");

  // State management for user and meeting data
  const [userName, setUserName] = React.useState<string>("");
  const [userJobTitle, setUserJobTitle] = React.useState<string>("");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(() => {
    if (!meetingId || !userId) {
      return "Missing Meeting or User ID";
    }
    return null;
  });

  const { data: meeting, isLoading } = useGetMeetingJoinInfo(meetingId, userId, joinCode);

  // Derive the layout from the meeting as soon as it's available, before the first paint, to avoid flashing the layout selector.
  const [lastProcessedMeeting, setLastProcessedMeeting] = React.useState(meeting);
  if (meeting !== lastProcessedMeeting) {
    setLastProcessedMeeting(meeting);
    if (!selectedLayout && meeting && meeting.meetingViews && meeting.meetingViews.length === 1) {
      setSelectedLayout(meeting.meetingViews[0].toLowerCase() as ExtendedMeetingLayout);
    }
  }

  // Amazon Chime meeting manager
  const meetingManager = useMeetingManager();

  /**
   * Handles successful meeting join response
   * Updates component state with user and meeting data from the response
   *
   * @param data - Response data from successful meeting join
   */
  const onJoinSuccess = (data: JoinResponse | null) => {

    if (data?.fullName) {
      setUserName(data.fullName);
    }
    if (data?.jobTitle) {
      setUserJobTitle(data.jobTitle);
    }

    const joinCodeTmp = data && data.joinCode ? data.joinCode : joinCodeProp || "";
    if (!joinCodeTmp || joinCodeTmp.length === 0) {
      setErrorMessage("Missing join code");
      return;
    }

    setJoinCode(joinCodeTmp);

    setGuestCredentials({
      meetingId,
      userId,
      joinCode: joinCodeTmp,
      authToken: undefined,
    });
  };

  /**
   * Handles meeting join errors
   * Logs the error and sets error message for user display
   *
   * @param error - Error object from failed meeting join attempt
   */
  const onJoinError = (error: unknown) => {
    console.error("Join error", error);

    const message = error instanceof Error ? error.message : "Failed to join meeting";
    setErrorMessage(message);
  };

  /**
   * Handles successful submission of the user's name and job title
   * from the UpdateUserInfoPanel, allowing the meeting to proceed.
   */
  const handleUserInfoUpdated = (fullName: string, jobTitle: string) => {
    setUserName(fullName);
    setUserJobTitle(jobTitle);
  };

  // Custom hook for managing meeting join process
  const { mutate: joinMeeting, isPending: isJoining } = useJoinMeeting(
    meetingManager,
    onJoinSuccess,
    onJoinError,
  );

  /**
   * Effect hook to handle meeting join process
   * Validates required parameters and initiates meeting join
   */
  useEffect(() => {
    // Don't proceed if there's already an error
    if (errorMessage !== null) {
      return;
    }

    // Check if a meeting layout has been selected before joining the meeting
    if (!selectedLayout) {
      return;
    }

    // Initiate meeting join process
    joinMeeting({
      meetingId,
      userId,
      joinCode,
      meetingType: typeFromLayout(selectedLayout),
    });
  }, [meetingId, userId, selectedLayout, joinMeeting]);

  const needsUserInfo = !(userName || meeting?.fullName) || !(userJobTitle || meeting?.jobTitle);

  return isLoading ? (
    <MeetingLoadingPage />
  ) : !selectedLayout ? (
    <MeetingLayoutSelectionPage
      onSelectLayout={(layout) => {
        setSelectedLayout(layout);
      }}
    />
  ) : (
    <MeetingInfoProvider
      meetingId={meetingId}
      userId={userId}
      userName={userName}
      joinType={typeFromLayout(selectedLayout)}
      isTraining={selectedLayout === "training"}
      joinCode={joinCode}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
    >
      <ChimeMessageProvider>
        {/* Meeting status monitoring component */}
        <MeetingStatusManager />

        {/* Render the appropriate meeting interface */}
        {errorMessage ? (
          <MeetingStatusPage
            errorMessage={errorMessage}
            isConnecting={errorMessage === "Connecting..."}
          />
        ) : selectedLayout ? (
          selectedLayout === "headset" ? (
            <HeadsetLayout />
          ) : selectedLayout === "phone" ? (
            <MobileUploadLayout />
          ) : selectedLayout === "training" ? (
            <TrainingLayout />
          ) : (
            isJoining ? (
              <MeetingStatusPage
                errorMessage="Connecting..."
                isConnecting={true}
              />
            ) : needsUserInfo ? (
              <UpdateUserInfoPage
                fullName={meeting?.fullName || ""}
                jobTitle={meeting?.jobTitle || ""}
                meetingId={meetingId}
                userId={userId}
                joinCode={joinCode}
                onSuccess={handleUserInfoUpdated}
              />
            ) : selectedLayout === "metro" ? (
              <MetroLayout />
            ) : (  // Default to remote layout if no other layout matches
              <RemoteLayout />
            )
          )
        ) : <MeetingLayoutSelectionPage
          onSelectLayout={(layout) => {
            setSelectedLayout(layout);
          }}
        />}
      </ChimeMessageProvider>
    </MeetingInfoProvider>
  );
};

export default MeetingPage;
